import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SkipLink from "@/components/SkipLink";
import SectionHeader from "@/components/SectionHeader";
import CTAButton from "@/components/CTAButton";
import { StructuredData, SEO } from "@/components/SEO";
import { ArrowRight } from "lucide-react";

export default function ParkClassesPage() {
  const breadcrumbData = {
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: "https://fiteasy.com",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Park Classes",
        item: "https://fiteasy.com/park-classes",
      },
    ],
  };

  const steps = [
    "Browse and discover classes near you in the FitEasy app—filter by type, time, location, and instructor ratings",
    "Book your preferred slot and complete secure payment through the app",
    "Arrive at the park 5 minutes early, scan your unique QR code to check in, and enjoy your workout",
    "Share your experience with a review to help others and earn rewards",
  ];

  return (
    <>
      <SEO
        title="Park Classes"
        description="Join outdoor park classes with expert instructors, easy bookings, and QR check-in through the FitEasy app. Bootcamps, yoga, mobility, and more in curated parks."
        path="/park-classes"
      />
      <StructuredData type="BreadcrumbList" data={breadcrumbData} />
      <div className="bg-gradient-to-b from-slate-50 to-blue-50 text-gray-900 min-h-screen">
        <SkipLink />
        <Header />

        <main id="main-content" className="mx-auto max-w-6xl px-4 pb-16 pt-10 sm:px-6 lg:px-8">
          <section aria-labelledby="park-classes-heading" className="mb-12 grid gap-8 md:grid-cols-[1.1fr_0.9fr] md:items-center">
            <div className="space-y-4">
              <SectionHeader
                id="park-classes-heading"
                label="Park Classes"
            title="Experience the energy of outdoor group fitness"
            description="FitEasy park classes transform beautiful outdoor spaces into your personal fitness studio. Join expert-led bootcamp sessions, yoga flows, HIIT workouts, mobility classes, and strength training sessions in carefully curated parks across your city. Enjoy the benefits of fresh air, natural sunlight, and a motivating community while achieving your fitness goals. All classes feature seamless QR check-in, transparent reviews, and flexible booking."
                className="mb-0"
              />
              <ol className="mt-4 space-y-3 text-sm text-gray-700" role="list">
                {steps.map((step, index) => (
                  <li key={index} className="flex items-start gap-3" role="listitem">
                    <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-blue-600 text-xs font-semibold text-white">
                      {index + 1}
                    </span>
                    <span className="leading-relaxed text-sm">{step}</span>
                  </li>
                ))}
              </ol>
              <div className="mt-6">
                <CTAButton
                  href="/programs"
                  variant="primary"
                  ariaLabel="Explore park classes in the FitEasy app"
                >
                  Explore Classes
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </CTAButton>
              </div>
            </div>
            <div className="relative h-64 w-full overflow-hidden rounded-3xl border border-blue-100 bg-white/80">
              <img
                src="/hero/hero_home_workout.png"
                alt="FitEasy park classes - Outdoor group fitness in curated parks"
                className="w-full h-full object-cover"
                loading="eager"
              />
            </div>
          </section>

          <section aria-labelledby="benefits-heading" className="grid gap-6 sm:grid-cols-3">
            <SectionHeader
              id="benefits-heading"
              label="Why Park Classes"
              title="What makes our classes special"
              className="sm:col-span-3 mb-0"
            />
            <article className="rounded-3xl border border-blue-100 bg-white/90 p-6 transition hover:shadow-lg focus-within:ring-2 focus-within:ring-blue-600 focus-within:ring-offset-2">
              <h2 className="font-display text-lg text-gray-900">
                Certified & verified instructors
              </h2>
              <p className="mt-2 text-sm text-gray-700 leading-relaxed">
                Every instructor undergoes a rigorous vetting process including certification verification, background checks, safety protocol training, and ongoing performance monitoring. We only work with professionals who have proven expertise, excellent client ratings, and a commitment to creating safe, effective, and enjoyable workout experiences.
              </p>
            </article>
            <article className="rounded-3xl border border-blue-100 bg-white/90 p-6 transition hover:shadow-lg focus-within:ring-2 focus-within:ring-blue-600 focus-within:ring-offset-2">
              <h2 className="font-display text-lg text-gray-900">
                Seamless QR check-in system
              </h2>
              <p className="mt-2 text-sm text-gray-700 leading-relaxed">
                Our innovative QR code check-in system makes attendance tracking effortless. Simply scan your unique code when you arrive—no waiting in lines, no manual sign-ins. This system also protects your spot in popular classes, helps instructors manage class sizes, and automatically updates your workout history in the app.
              </p>
            </article>
            <article className="rounded-3xl border border-blue-100 bg-white/90 p-6 transition hover:shadow-lg focus-within:ring-2 focus-within:ring-blue-600 focus-within:ring-offset-2">
              <h2 className="font-display text-lg text-gray-900">
                Transparent community reviews
              </h2>
              <p className="mt-2 text-sm text-gray-700 leading-relaxed">
                Make informed decisions by reading authentic reviews from fellow members. See ratings for class difficulty, instructor style, class atmosphere, and overall experience. After attending, share your own feedback to help build our community and earn rewards. All reviews are verified to ensure authenticity.
              </p>
            </article>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}

